import { z } from "zod";
import type { FieldPath } from "react-hook-form";
import type { OpportunityDraftFinding } from "@altimateai/ui-components/chatbotV2";

/**
 * Studio → User opportunity, draft-a-form flow (flag `enableStudioOpportunities`,
 * AI-7498). The agent streams a drafted finding on the `opportunity_draft`
 * channel; the FE renders it as an editable form and publishes on Submit.
 * Field vocabulary mirrors the backend `OpportunityFinding` schema.
 */

export const OPPORTUNITY_EFFORTS = ["low", "medium", "high"] as const;

export type OpportunityEffort = (typeof OPPORTUNITY_EFFORTS)[number];

export const findingResourceSchema = z.object({
  resource_name: z
    .string()
    .nullish()
    .transform(value => value ?? ""),
  resource_type: z
    .string()
    .nullish()
    .transform(value => value ?? ""),
  resource_uri: z
    .string()
    .nullish()
    .transform(value => value ?? ""),
  resource_id: z
    .string()
    .nullish()
    .transform(value => value ?? ""),
  // The account/instance the resource belongs to. Drives the Account dropdown
  // and scopes the RBAC-aware inventory lookups. Nullable — a cold draft may
  // name a resource before its instance is resolved.
  instance_id: z.coerce.number().nullish(),
  verified: z.boolean().default(false),
});

/**
 * The full finding sent to the submit endpoint. Parsing the streamed draft
 * through this schema fills any field the producer left off with a default,
 * so the form never crashes on a partial draft.
 */
// `.default()` only fires on `undefined` — an agent streaming explicit
// `null`s would fail plain `z.string().default()`. Normalize nulls first.
const nullableString = (fallback: string) =>
  z
    .string()
    .nullish()
    .transform(value => value ?? fallback);

export const opportunityFindingSchema = z.object({
  title: nullableString(""),
  description: nullableString(""),
  resource_name: nullableString(""),
  resource_type: nullableString(""),
  estimated_savings_usd_per_month: z.coerce
    .number()
    .nullish()
    .transform(value => value ?? 0),
  effort: nullableString("medium"),
  resources: z
    .array(findingResourceSchema)
    .nullish()
    .transform(value => value ?? []),
  turn_uuid: nullableString(""),
  // Stamped onto the persisted draft block by a successful submit, so a
  // re-fetched session renders the Published card instead of a fresh draft.
  published: z.object({ opportunity_id: z.string(), instance_id: z.number() }).nullish(),
  // Stamped by `POST /studio/opportunities/discard`, so a re-fetched session
  // renders the collapsed Rejected card instead of a live editable draft.
  discarded: z.object({ at: z.string().nullish(), by: z.coerce.string().nullish() }).nullish(),
});

export type OpportunityFinding = z.infer<typeof opportunityFindingSchema>;

/** Response of `POST /studio/opportunities/submit`. */
export const submitStudioOpportunityResponseSchema = z.object({
  opportunity_id: z.string(),
  instance_id: z.number(),
  already_existed: z.boolean().default(false),
});

export type SubmitStudioOpportunityResponse = z.infer<typeof submitStudioOpportunityResponseSchema>;

/** A single gate failure from a 422 submit response. */
export const studioOpportunityFieldErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
});

/**
 * The `detail` payload of a 422: `{ errors: [{ code, message }] }`.
 *
 * The global axios interceptor (`api_helper.ts`) rejects with
 * `error.response.data.detail` rather than the `AxiosError`, so this — not the
 * wrapped body — is what a failed submit actually throws.
 */
export const studioOpportunityErrorDetailSchema = z.object({
  errors: z.array(studioOpportunityFieldErrorSchema).min(1),
});

/** The full 422 body: `{ detail: { errors: [{ code, message }] } }`. */
export const submitStudioOpportunityErrorSchema = z.object({
  detail: studioOpportunityErrorDetailSchema,
});

export type StudioOpportunityFieldError = z.infer<typeof studioOpportunityFieldErrorSchema>;

/**
 * What the savings claim was judged against (AI-8313). Every amount is USD by
 * definition — the backend fields are named `_usd` and the spend baseline is
 * read in USD — so these are NOT converted to the tenant's display currency.
 */
// Every field is null-TOLERANT, not just optional. `.default()` fires only on
// `undefined`, so an explicit `null` would fail `z.string()` — and because the
// `savings` key is then present-but-invalid, the `.nullish()` on the response
// does NOT rescue it: the whole validate response fails `safeParse` and the
// form loses the verdict again, which is the bug this schema was added to fix.
export const savingsValidationReportSchema = z.object({
  evidence_source: nullableString("none"),
  claimed_savings_usd_per_month: z.coerce
    .number()
    .nullish()
    .transform(value => value ?? 0),
  // Null when no baseline could be read (an unsupported resource type, or a
  // failed cost lookup) — never evidence that the claim is wrong.
  resource_monthly_spend_usd: z.coerce.number().nullish(),
  // The ClickHouse table the baseline came from, for support triage.
  spend_source: nullableString(""),
  spend_window_days: z.coerce
    .number()
    .nullish()
    .transform(value => value ?? 30),
  spend_days_observed: z.coerce
    .number()
    .nullish()
    .transform(value => value ?? 30),
  // The bound the gate actually applied: the raw sum projected to a full window.
  monthly_run_rate_usd: z.coerce.number().nullish(),
  // Claim as a share of `monthly_run_rate_usd`, so a claim that passed never
  // reads back as over 100%. Null when spend is unknown or zero.
  savings_pct_of_spend: z.coerce.number().nullish(),
  session_max_dollar_figure: z.coerce.number().nullish(),
});

export type SavingsValidationReport = z.infer<typeof savingsValidationReportSchema>;

/**
 * Response of `POST /studio/opportunities/validate` — the pre-submit smoke test
 * (AI-7498). A 200 means the deterministic gates passed; `errors` and
 * `warnings` carry any blocking / non-blocking findings. A gate failure comes
 * back as a 422 with the same `{ detail: { errors } }` shape as submit, parsed
 * by `parseStudioOpportunitySubmitErrors`.
 *
 * `savings` MUST stay on this schema: anything absent here is stripped by
 * `patchWithZodSchema` before the form ever sees it, which is how the entire
 * AI-8313 advisory tier came to be computed and then silently discarded.
 */
export const validateStudioOpportunityResponseSchema = z.object({
  valid: z.boolean().default(true),
  errors: z.array(studioOpportunityFieldErrorSchema).default([]),
  warnings: z.array(studioOpportunityFieldErrorSchema).default([]),
  savings: savingsValidationReportSchema.nullish(),
});

export type ValidateStudioOpportunityResponse = z.infer<
  typeof validateStudioOpportunityResponseSchema
>;

/**
 * The resource types the inventory dropdowns support. `query` is transient —
 * it has no inventory list, so its name is entered as free text.
 */
export const OPPORTUNITY_RESOURCE_TYPES = [
  "warehouse",
  "table",
  "query",
  "dbt_model",
  "cluster",
  "job",
] as const;

export type OpportunityResourceType = (typeof OPPORTUNITY_RESOURCE_TYPES)[number];

/**
 * The resource types a given account can actually publish (AI-8314).
 *
 * The list used to be one hardcoded array with no provider awareness, so a
 * Snowflake account offered nothing Databricks and a Databricks account
 * offered Snowflake-only types that its resolver rejects.
 *
 * `user` and `role` were offered by both and publishable by neither: the
 * resolver has no branch for either (`_resolve_snowflake` falls through to
 * `verified=false` → 422), `ROLE` has no redirect target at all so a published
 * card would dead-link, `USER` redirects into `/bi-reports/users/` — a
 * different entity domain from the Snowflake users the picker offered — and
 * neither has a spend baseline, so every claim would come back unverifiable.
 * Production carries zero ROLE opportunities, and every USER row is a *linked*
 * resource on a query opportunity, never a primary. They are dropped rather
 * than left selectable-but-unpublishable.
 */
export const resourceTypesForProvider = (
  provider: string | undefined
): readonly OpportunityResourceType[] =>
  provider === "databricks"
    ? (["warehouse", "cluster", "job", "query"] as const)
    : (["warehouse", "table", "query", "dbt_model"] as const);

/**
 * Which types get an inventory picker rather than a free-text box.
 *
 * An ALLOWLIST on purpose. This was `!== "query"`, a denylist, so every type
 * added afterwards silently claimed a picker it had no fetcher for and
 * rendered an empty dropdown with no way to type a value
 * (`AsyncEntitySelector` falls through to `default: return { items: [] }`).
 */
export const isInventoryResourceType = (resourceType: string): boolean =>
  resourceType === "warehouse" || resourceType === "table";

/**
 * One resource row of the draft form. `resources[0]` is the primary target;
 * the rest (`relevant: true`) are linked resources shown on the same card.
 */
export const draftResourceSchema = z.object({
  resource_type: z.string(),
  resource_name: z.string(),
  resource_id: z.string().default(""),
  resource_uri: z.string().default(""),
  relevant: z.boolean(),
});

export type DraftResource = z.infer<typeof draftResourceSchema>;

/** The editable fields the user can correct before publishing. */
export const opportunityDraftFormSchema = z.object({
  title: z.string().min(1, "Title is required"),
  // The account/instance every resource on this card belongs to. Stored as a
  // string because the Select value is a string; mapped back to a number when
  // written into `resources[].instance_id`.
  account_id: z.string(),
  resources: z.array(draftResourceSchema).min(1, "At least one resource is required"),
  description: z.string(),
  estimated_savings_usd_per_month: z.coerce.number().min(0, "Savings must be zero or positive"),
  effort: z.enum(OPPORTUNITY_EFFORTS),
});

export type OpportunityDraftFormValues = z.infer<typeof opportunityDraftFormSchema>;

/**
 * Maps a gate `error.code` from a 422 response to the form field it belongs to,
 * so a publish rejection surfaces as inline validation instead of a chat wall.
 * Unmapped codes fall back to a form-level alert.
 */
export const ERROR_CODE_TO_FIELD: Record<string, FieldPath<OpportunityDraftFormValues>> = {
  // Primary resource (AI-8314 adds more resolver codes to this group).
  resource_not_verified: "resources.0.resource_name",
  resource_uri_unresolved: "resources.0.resource_name",
  // dbt models resolve against the dbt artifact catalog (`dbt_node`), not the
  // dataset they materialize into — that table is often absent (AI-8314).
  dbt_model_not_found: "resources.0.resource_name",
  dbt_model_ambiguous: "resources.0.resource_name",
  // The primary resource is immutable on an edit: `opportunity_id` derives from
  // it, so changing it would strand the opportunity's comments and audit trail.
  primary_resource_immutable: "resources.0.resource_name",
  title_required: "title",
  // The description is published verbatim and is what a customer reads, so an
  // empty, title-duplicating or placeholder one is rejected (AI-8313).
  description_required: "description",
  description_too_thin: "description",
  description_duplicates_title: "description",
  // Savings bounded by the resource's measured spend (AI-8313). Only these two
  // block; the softer savings findings come back as warnings, never as errors.
  savings_negative: "estimated_savings_usd_per_month",
  savings_exceeds_spend: "estimated_savings_usd_per_month",
};

/**
 * Response of `POST /studio/opportunities/discard` — rejecting an in-flight
 * DRAFT (never a published opportunity). Idempotent: `discarded: false` means
 * nothing matched, which is not an error — the client must not collapse the
 * card in that case or it strands a still-live draft behind a dead one.
 */
export const discardStudioOpportunityResponseSchema = z.object({
  discarded: z.boolean().default(false),
  resource_name: z.string().default(""),
});

export type DiscardStudioOpportunityResponse = z.infer<
  typeof discardStudioOpportunityResponseSchema
>;

/** A blank finding — RHF default values before a draft arrives. */
export const emptyFinding = (): OpportunityFinding => opportunityFindingSchema.parse({});

/** The Published-card state carried by a previously-published draft, if any. */
export const publishedFromFinding = (
  finding: OpportunityFinding
): SubmitStudioOpportunityResponse | null =>
  finding.published
    ? {
        opportunity_id: finding.published.opportunity_id,
        instance_id: finding.published.instance_id,
        already_existed: true,
      }
    : null;

/** Normalize a streamed draft (possibly partial) into a full finding. */
export const parseFinding = (
  draft: OpportunityDraftFinding | OpportunityFinding
): OpportunityFinding => opportunityFindingSchema.parse(draft);

const asOption = <T extends readonly string[]>(
  options: T,
  value: string,
  fallback: T[number]
): T[number] => ((options as readonly string[]).includes(value) ? (value as T[number]) : fallback);

/**
 * Prefill the editable form from a finding.
 *
 * The producer serializes the backend `OpportunityFinding`, whose `resources[]`
 * carries the primary target first and any linked (relevant) resources after.
 * The Account dropdown defaults to the primary's `instance_id`. When a draft
 * predates the nested model (only the flat `resource_name`/`resource_type`), a
 * single primary row is synthesized so the form always renders at least one row.
 */
export const findingToFormValues = (finding: OpportunityFinding): OpportunityDraftFormValues => {
  const baseResources: OpportunityFinding["resources"] = finding.resources.length
    ? finding.resources
    : [
        {
          resource_name: finding.resource_name,
          resource_type: finding.resource_type,
          resource_uri: "",
          resource_id: "",
          instance_id: null,
          verified: false,
        },
      ];
  const primary = baseResources[0];

  return {
    title: finding.title,
    account_id: primary?.instance_id != null ? String(primary.instance_id) : "",
    resources: baseResources.map((resource, index) => ({
      resource_type: resource.resource_type || (index === 0 ? finding.resource_type : ""),
      resource_name: resource.resource_name,
      resource_id: resource.resource_id,
      resource_uri: resource.resource_uri,
      relevant: index > 0,
    })),
    description: finding.description,
    effort: asOption(OPPORTUNITY_EFFORTS, finding.effort, "medium"),
    estimated_savings_usd_per_month: finding.estimated_savings_usd_per_month,
  };
};

/**
 * Merge the user's edits back over the drafted finding for submission.
 *
 * The server resolves and verifies each target purely from `resources[]`
 * (`resources[0]` is primary, the rest are relevant) scoped by its
 * `instance_id`, so every edited row MUST be written back into `resources[]` —
 * otherwise a correction in the form would silently publish the originally
 * drafted resource. The top-level `resource_name`/`resource_type` mirror the
 * primary so the backend's primary-picker still lines up. `resource_uri` /
 * `resource_id` are kept only for rows the user left untouched; the server
 * re-verifies every submission regardless, so `verified` is advisory.
 */
export const formValuesToFinding = (
  base: OpportunityFinding,
  values: OpportunityDraftFormValues
): OpportunityFinding => {
  const instanceId = values.account_id ? Number(values.account_id) : null;

  const resources = values.resources.map((row, index) => {
    const original = base.resources[index];
    const unchanged =
      original?.resource_name === row.resource_name &&
      original?.resource_type === row.resource_type;

    return {
      resource_name: row.resource_name,
      resource_type: row.resource_type,
      resource_uri: unchanged ? original.resource_uri : row.resource_uri || "",
      resource_id: unchanged ? original.resource_id : row.resource_id || "",
      instance_id: instanceId,
      verified: unchanged ? original.verified : false,
    };
  });
  const primary = resources[0];

  return {
    ...base,
    title: values.title,
    resource_name: primary?.resource_name || "",
    resource_type: primary?.resource_type || "",
    description: values.description,
    effort: values.effort,
    estimated_savings_usd_per_month: values.estimated_savings_usd_per_month,
    resources,
  };
};

// "dbt" is lowercase everywhere else in the product (the /dbt_models pages
// brand it that way), and the free-text label on the very same row says
// "dbt model" — a naive capitalize would disagree with itself inside one row.
const LOWERCASE_FIRST_WORDS = new Set(["dbt"]);

export const enumLabel = (value: string) => {
  const spaced = value.replaceAll("_", " ");
  const [first] = spaced.split(" ");
  if (LOWERCASE_FIRST_WORDS.has(first)) return spaced;
  return spaced.replace(/^\w/, char => char.toUpperCase());
};
